import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation, nextIncompleteRoute } from '../lib/wizardSteps';
import { saveAndNavigate } from '../lib/navigationHelpers';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, backUrlFor, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { useCreditPricing } from '../hooks/useCreditPricing';
import { Check, RefreshCw, CreditCard as Edit3, Sparkles, ChevronDown, ChevronUp, Upload } from 'lucide-react';

export function SelectImage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, refreshSession } = useSession();
  const sessionId = sessionIdFromLocation(location);
  const [completing, setCompleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [generationInfo, setGenerationInfo] = useState<{
    current: number;
    max: number;
    remaining: number;
  } | null>(null);
  const [loadingGenInfo, setLoadingGenInfo] = useState(true);
  const [showCustomPromptTips, setShowCustomPromptTips] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [useCustomPromptMode, setUseCustomPromptMode] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { pricing } = useCreditPricing();
  const imageCost = pricing['images_generate'] ?? 400;

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sid: string) => {
    try {
      setLoading(true);

      const { data: sessionData } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sid)
        .maybeSingle();

      if (sessionData) {
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', sessionData.user_id)
          .maybeSingle();

        setDashboardSession(sessionData);
        setDashboardUser(userData);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const activeUser = dashboardUser || user;
  const { handleStepClick } = useWizardNavigation(activeSession);
  const images: string[] = Array.isArray(activeSession?.images) ? activeSession.images : [];

  const handleSelectImage = async (imageUrl: string) => {
    if (completing || !activeSession?.id || !activeUser) return;

    if (imageUrl === activeSession.selected_image_url) {
      navigate(wizardUrl(nextIncompleteRoute(activeSession), activeSession.id));
      return;
    }

    try {
      setCompleting(true);
      console.log('Downloading and storing selected image:', imageUrl);

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const authToken = authSession?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-and-store-image`;
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      };

      const downloadResponse = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          imageUrl,
          userId: activeUser.id,
          sessionId: activeSession.id,
          context: 'wizard',
        }),
      });

      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text();
        console.error('Image download failed:', {
          status: downloadResponse.status,
          statusText: downloadResponse.statusText,
          response: errorText,
        });
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          throw new Error(`Failed to store image (${downloadResponse.status}): ${errorText}`);
        }
        throw new Error(errorData.error || 'Failed to store image');
      }

      const { publicUrl } = await downloadResponse.json();
      console.log('Image stored successfully:', publicUrl);

      await saveAndNavigate(
        activeSession.id,
        {
          selected_image_url: publicUrl,
          ...(activeSession.state === 'completed' ? {} : { state: 'image_selected' }),
        },
        ['selected_image_url'],
        navigate,
        wizardUrl(WIZARD_PATHS.preview, activeSession.id),
      );

      if (refreshSession) {
        await refreshSession();
      }
    } catch (error) {
      console.error('Error selecting image:', error);
      alert('Er ging iets mis bij het opslaan van de afbeelding. Probeer het opnieuw.');
      setCompleting(false);
    }
  };

  const handleBack = () => {
    const back = backUrlFor(WIZARD_PATHS.selectImage, activeSession?.id);
    if (back) navigate(back);
  };

  const handleRegenerateImages = () => {
    navigate(wizardUrl(WIZARD_PATHS.imageGenerate, activeSession?.id), {
      state: { isRegeneration: true },
    });
  };

  const handleCustomPromptGenerate = () => {
    if (!customPrompt.trim()) return;

    navigate(wizardUrl(WIZARD_PATHS.imageGenerate, activeSession?.id), {
      state: {
        isRegeneration: true,
        customPrompt: customPrompt.trim(),
        forceCustomPrompt: true,
      },
    });
  };

  React.useEffect(() => {
    const fetchGenerationInfo = async () => {
      if (!activeSession?.id) return;

      try {
        const { data: sessionData } = await supabase
          .from('sessions')
          .select('image_generation_count')
          .eq('id', activeSession.id)
          .maybeSingle();

        const { data: configData } = await supabase
          .from('system_config')
          .select('value')
          .eq('key', 'max_image_generations')
          .maybeSingle();

        const currentCount = sessionData?.image_generation_count || 0;
        const maxGenerations = parseInt(configData?.value || '3');

        setGenerationInfo({
          current: currentCount,
          max: maxGenerations,
          remaining: maxGenerations - currentCount,
        });
      } catch (error) {
        console.error('Error fetching generation info:', error);
      } finally {
        setLoadingGenInfo(false);
      }
    };

    if (!loading) {
      fetchGenerationInfo();
    }
  }, [activeSession?.id, loading]);

  if (completing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Afbeelding kiezen"
          onClose={() => navigate(WIZARD_EXIT_ROUTE)}
          hideSaveStatus
        />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <LoadingSpinner text="Afbeelding opslaan..." />
        </main>
      </div>
    );
  }

  const steps = getWizardSteps(activeSession, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <FocusHeader
          title="Afbeelding kiezen"
          onClose={() => navigate(WIZARD_EXIT_ROUTE)}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <FocusHeader
        title="Afbeelding kiezen"
        onBack={handleBack}
        onClose={() => navigate(WIZARD_EXIT_ROUTE)}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <StepIndicator steps={steps} onStepClick={handleStepClick} />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2 flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              Kies je favoriete afbeelding
            </h1>
            <p className="text-gray-600">Deze komt bovenaan je blog</p>
          </div>
        </div>

        {images.length === 0 && (
          <div className="card text-center space-y-4">
            <p className="text-gray-700">Er zijn nog geen afbeeldingen voor deze blog.</p>
            <button
              onClick={() => navigate(wizardUrl(WIZARD_PATHS.imageGenerate, activeSession?.id))}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Afbeeldingen laten maken
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {images.map((imageUrl: string, index: number) => (
            <button
              key={index}
              onClick={() => handleSelectImage(imageUrl)}
              disabled={completing}
              className="card p-3 hover:border-[#231F20] hover:shadow-lg transition-all overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img
                src={imageUrl}
                alt={`Option ${index + 1}`}
                className="w-full h-auto rounded-lg"
              />
            </button>
          ))}
        </div>

        <div className="card p-6 bg-gray-50 border-gray-200">
          <button
            onClick={() => setShowUploadSection(!showUploadSection)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3">
              <Upload className="w-5 h-5 text-[#231F20]" />
              <div>
                <div className="font-semibold text-[#231F20]">Of upload eigen afbeelding</div>
                <div className="text-sm text-gray-600">Upload je eigen afbeelding in plaats van AI-generatie</div>
              </div>
            </div>
            {showUploadSection ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showUploadSection && (
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#231F20] transition-colors cursor-pointer"
                onClick={() => document.getElementById('select-file-upload')?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-[#231F20]', 'bg-gray-50');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-[#231F20]', 'bg-gray-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-[#231F20]', 'bg-gray-50');
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith('image/')) {
                    handleFileSelect(file);
                  }
                }}
              >
                {uploadPreview ? (
                  <div className="space-y-4">
                    <img
                      src={uploadPreview}
                      alt="Preview"
                      className="max-h-64 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-gray-600">
                      {selectedFile?.name} ({(selectedFile?.size! / 1024 / 1024).toFixed(2)} MB)
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        setUploadPreview(null);
                      }}
                      className="text-sm text-[#231F20] hover:underline"
                    >
                      Andere afbeelding kiezen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="text-gray-600">
                      <span className="font-semibold text-[#231F20]">Klik om te uploaden</span> of sleep je afbeelding hier
                    </p>
                    <p className="text-sm text-gray-500">PNG, JPG, WebP tot 10MB</p>
                  </div>
                )}
              </div>

              <input
                id="select-file-upload"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                }}
              />

              {selectedFile && (
                <button
                  onClick={handleUploadAndSelectImage}
                  disabled={isUploading || completing}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? 'Uploaden...' : 'Gebruik deze afbeelding'}
                </button>
              )}
            </div>
          )}
        </div>

        {!loadingGenInfo && generationInfo && (
          <div className="card p-6 bg-gray-50 border-gray-200">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#231F20] mb-2">
                  Niet tevreden met de afbeeldingen?
                </h3>
                <p className="text-gray-600">
                  Kies tussen automatische AI-generatie of gebruik je eigen prompt
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Elke nieuwe generatie kost {imageCost} credits en levert 4 nieuwe afbeeldingen op.
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Sparkles className="w-5 h-5 text-[#231F20] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#231F20] mb-1">Automatisch regenereren</div>
                      <p className="text-sm text-gray-600 mb-3">
                        AI analyseert je artikel en genereert nieuwe geoptimaliseerde afbeeldingen
                      </p>
                      {generationInfo.remaining > 0 ? (
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">{generationInfo.remaining}</span> {generationInfo.remaining === 1 ? 'regeneratie' : 'regeneraties'} beschikbaar
                        </p>
                      ) : (
                        <p className="text-sm text-amber-700 font-medium">
                          Je kunt maximaal 3 keer opnieuw hergeneren. Nog niet tevreden? Geef dan hieronder je eigen omschrijving op.
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleRegenerateImages}
                    disabled={completing || generationInfo.remaining === 0}
                    className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Genereer nieuwe ({imageCost} credits)
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-sm text-gray-500 font-medium">of</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Edit3 className="w-5 h-5 text-[#231F20] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-[#231F20] mb-1">Gebruik eigen omschrijving</div>
                      <p className="text-sm text-gray-600 mb-3">
                        Schrijf zelf een specifieke prompt voor volledige controle over het resultaat
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <textarea
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      placeholder="Vrouw van 50 zit in een koffietent te typen op haar Macbook, natuurlijk licht van zijraam, gefotografeerd over haar schouder, gezicht niet zichtbaar, notitieblok en telefoon op tafel, wazige achtergrond met café-interieur, warme kleuren"
                      className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#231F20] focus:border-transparent resize-none text-sm"
                    />
                    <div className="flex items-start justify-between">
                      <div className="text-sm text-gray-500">
                        {customPrompt.length} karakters
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowCustomPromptTips(!showCustomPromptTips)}
                        className="flex items-center gap-1 text-sm text-[#231F20] hover:underline"
                      >
                        {showCustomPromptTips ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Verberg tips
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Toon tips
                          </>
                        )}
                      </button>
                    </div>

                    {showCustomPromptTips && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-2">
                        <div className="font-semibold text-blue-900">Tips voor effectieve prompts:</div>
                        <ul className="space-y-1 text-blue-800 list-disc list-inside">
                          <li>Beschrijf specifieke onderwerpen en scènes</li>
                          <li>Voeg compositie details toe (bijv. close-up, overhead angle)</li>
                          <li>Specificeer belichting (natural light, soft lighting, etc.)</li>
                          <li>Gebruik stijl keywords (professional, candid, corporate, modern)</li>
                          <li>Vermijd tekst in de afbeelding (gebruik "no text overlay")</li>
                          <li>Denk aan: wie, wat, waar, hoe het eruitziet</li>
                        </ul>
                      </div>
                    )}

                    <button
                      onClick={handleCustomPromptGenerate}
                      disabled={completing || !customPrompt.trim()}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles className="w-4 h-4" />
                      Genereer met eigen prompt ({imageCost} credits)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </main>
    </div>
  );

  function handleFileSelect(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      alert('Bestand is te groot. Maximale grootte is 10MB.');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }

  async function handleUploadAndSelectImage() {
    if (!selectedFile || !activeSession || !activeUser) return;

    try {
      setIsUploading(true);

      const { compressImage } = await import('../lib/imageCompression');
      const { uploadToStorage } = await import('../lib/storageUpload');

      const compressed = await compressImage(selectedFile, 1920, 0.5, 'webp', 0.85);

      const result = await uploadToStorage(
        compressed.blob,
        activeUser.id,
        'wizard',
        { sessionId: activeSession.id }
      );

      console.log('Image uploaded successfully:', result.publicUrl);

      await saveAndNavigate(
        activeSession.id,
        {
          selected_image_url: result.publicUrl,
          ...(activeSession.state === 'completed' ? {} : { state: 'image_selected' }),
        },
        ['selected_image_url'],
        navigate,
        wizardUrl(WIZARD_PATHS.preview, activeSession.id),
      );

      if (refreshSession) {
        await refreshSession();
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Er ging iets mis bij het uploaden. Probeer het opnieuw.');
      setIsUploading(false);
    }
  }
}
